/**
 * Per-kind starter content for the Document Templates composer's Body
 * field — real letter/lease shape (headings, paragraphs, lists) with actual
 * merge-field chips already inserted, not a blank box. The firm's own
 * language and counsel review still have to replace the bracketed
 * `[placeholders]` before anything here should reach a tenant; this exists
 * so staff start from a document-shaped draft instead of a blank textarea.
 *
 * Chips are authored as the same span markup `MergeFieldNode.renderHTML`
 * produces (see merge-field-node.ts's doc comment) so loading this content
 * into the editor seeds real, labeled chips — not raw `{{token}}` text.
 */

import {
  LEASE_MERGE_TOKEN_GROUPS,
  NOTICE_MERGE_TOKEN_GROUPS,
  TRANSACTIONAL_EMAIL_TOKEN_GROUPS,
  type DocumentTemplateKind,
} from 'shared';

const TOKEN_LABELS = new Map<string, string>();
for (const group of [
  ...NOTICE_MERGE_TOKEN_GROUPS,
  ...LEASE_MERGE_TOKEN_GROUPS,
  ...TRANSACTIONAL_EMAIL_TOKEN_GROUPS,
]) {
  for (const def of group.tokens) TOKEN_LABELS.set(def.token, def.label);
}

function chip(token: string): string {
  const label = TOKEN_LABELS.get(token) ?? token;
  return `<span data-merge-field="${token}" data-merge-field-label="${label}" class="merge-field-chip">{{${token}}}</span>`;
}

export const SAMPLE_BODY_TEMPLATES: Record<DocumentTemplateKind, string> = {
  DEPOSIT_NOTICE: `
    <p>Dear ${chip('tenant.names')},</p>
    <p>This letter is to notify you, in accordance with Michigan law (MCL 554.603), that your security
    deposit for ${chip('unit.label')} at ${chip('property.name')} has been received and is being held at
    [Name of Financial Institution], [Address of Financial Institution].</p>
    <p>Notice date: ${chip('notice.triggeredOn')}.</p>
    <p>If you have any questions, please contact us at ${chip('company.phone')} or ${chip('company.contactEmail')}.</p>
    <p>Sincerely,<br>${chip('company.signatoryName')}, ${chip('company.signatoryTitle')}<br>${chip('company.legalName')}</p>
  `,

  DAMAGES_NOTICE: `
    <h2>Notice of Damages and Security Deposit Disposition</h2>
    <p>Dear ${chip('tenant.names')},</p>
    <p>This notice concerns the security deposit for ${chip('unit.label')} at ${chip('property.name')},
    following the end of your tenancy on ${chip('notice.triggeredOn')}.</p>
    <p>In accordance with Michigan law (MCL 554.609), an itemized statement of damages is provided below:</p>
    <ul>
      <li>[Description of damage] — $[amount]</li>
      <li>[Description of damage] — $[amount]</li>
    </ul>
    <p><strong>Total deductions:</strong> $[amount]</p>
    <p><strong>Balance due:</strong> $[amount]</p>
    <p>You have <strong>7 days</strong> from the date of this notice to dispute these deductions in writing.</p>
    <p>Sincerely,<br>${chip('company.signatoryName')}<br>${chip('company.legalName')}</p>
  `,

  MOVE_IN_CHECKLIST: `
    <h2>Move-In Checklist</h2>
    <p>Dear ${chip('tenant.names')},</p>
    <p>Enclosed are two copies of the move-in condition checklist for ${chip('unit.label')} at
    ${chip('property.name')}. Please inspect the unit, note any existing damage or issues on both copies,
    sign, and return one copy to us within 7 days of move-in. Keep the second copy for your own records.</p>
    <p>Sincerely,<br>${chip('company.legalName')}</p>
  `,

  LEASE_RENEWAL: `
    <h2>Lease Renewal Offer</h2>
    <p>Dear ${chip('tenant.names')},</p>
    <p>Your current lease for ${chip('unit.label')} at ${chip('property.name')} is scheduled to end soon.
    We would like to offer you the opportunity to renew.</p>
    <p>Renewal terms:</p>
    <ul>
      <li>New term: [start date] through [end date]</li>
      <li>Monthly rent: $[amount]</li>
    </ul>
    <p>Please let us know by [response deadline] whether you would like to renew. We can be reached at
    ${chip('company.phone')} or ${chip('company.contactEmail')}.</p>
    <p>Sincerely,<br>${chip('company.signatoryName')}<br>${chip('company.legalName')}</p>
  `,

  RENT_INCREASE: `
    <h2>Notice of Rent Increase</h2>
    <p>Dear ${chip('tenant.names')},</p>
    <p>This letter is to notify you that, effective [effective date], the monthly rent for
    ${chip('unit.label')} at ${chip('property.name')} will increase from $[current amount] to $[new amount].</p>
    <p>All other terms of your lease remain unchanged. If you have any questions, please contact us at
    ${chip('company.phone')}.</p>
    <p>Sincerely,<br>${chip('company.signatoryName')}<br>${chip('company.legalName')}</p>
  `,

  OTHER: `
    <h2>${chip('notice.kind')}</h2>
    <p>Dear ${chip('tenant.names')},</p>
    <p>This letter is regarding ${chip('unit.label')} at ${chip('property.name')}.</p>
    <p>[Body of notice.]</p>
    <p>Sincerely,<br>${chip('company.signatoryName')}<br>${chip('company.legalName')}</p>
  `,

  // Token mismatch is deliberate, not an oversight: this generator isn't
  // wired to the merge system yet (see the "not yet merge-field-wired" note
  // shown above the Insert Field dropdown for this kind), and the notice
  // tokens available (tenant.names, etc.) describe a lease's tenant, not an
  // applicant — using them here would be actively misleading. Only the
  // company chips, which are correct regardless, are used; applicant/
  // criterion specifics stay bracketed placeholders until real wiring exists.
  DECLINE_NOTICE: `
    <h2>Application Decision</h2>
    <p>Dear [Applicant Name],</p>
    <p>Thank you for applying to lease [Unit / Property]. After careful review, we are unable to offer you
    this rental at this time.</p>
    <p>This decision was based on the following published screening criterion: [Criterion Description].</p>
    <p>If this decision was based in whole or in part on information from a consumer report, you have the
    right under the Fair Credit Reporting Act to obtain a free copy of that report from the reporting
    agency and to dispute its accuracy.</p>
    <p>If you have any questions, please contact us at ${chip('company.phone')} or ${chip('company.contactEmail')}.</p>
    <p>Sincerely,<br>${chip('company.signatoryName')}<br>${chip('company.legalName')}</p>
  `,

  // No forwarding-address clause here — that's the dedicated, separately
  // enforced field (see DocumentTemplateList.tsx), not part of the body.
  LEASE_AGREEMENT: `
    <h2>Parties and Premises</h2>
    <p>This Lease Agreement is entered into between ${chip('company.legalName')} ("Landlord") and
    ${chip('tenant.names')} ("Tenant"), for the premises located at ${chip('unit.label')},
    ${chip('property.name')}.</p>

    <h2>Term</h2>
    <p>The term of this lease begins on ${chip('lease.startDate')} and ends on ${chip('lease.endDate')},
    unless earlier terminated as provided herein.</p>

    <h2>Rent</h2>
    <p>Tenant agrees to pay monthly rent of ${chip('lease.monthlyRent')}, due on the
    ${chip('lease.rentDueDay')} of each month. A late fee of ${chip('company.lateFee5DayAmount')} applies once
    rent is 5 days late, with an additional ${chip('company.lateFee10DayAmount')} if still unpaid at 10 days
    late.</p>

    <h2>Security Deposit</h2>
    <p>Tenant has paid a security deposit of ${chip('lease.securityDeposit')}, to be held and returned in
    accordance with Michigan law (MCL 554.602 et seq.).</p>

    <h2>Use of Premises</h2>
    <p>The premises shall be used solely as a private residence for Tenant and immediate household members
    named on this lease.</p>

    <h2>Household</h2>
    <p>Dependents residing at the premises: ${chip('household.dependents')}.</p>
    <p>Pets kept at the premises: ${chip('household.pets')}.</p>

    <h2>Maintenance and Repairs</h2>
    <ul>
      <li>Tenant shall keep the premises clean and promptly report needed repairs.</li>
      <li>Landlord shall maintain the premises in compliance with applicable housing codes.</li>
    </ul>

    <h2>Landlord Entry</h2>
    <p>Landlord may enter the premises with reasonable notice to inspect, make repairs, or show the unit to
    prospective tenants or buyers.</p>

    <h2>Default</h2>
    <p>Failure to pay rent when due, or to comply with any material term of this lease, may result in
    termination of tenancy in accordance with Michigan law.</p>

    <h2>Owner of Record</h2>
    <p>${chip('owner.names')}, ${chip('owner.mailingAddress')}.</p>

    <h2>Signatures</h2>
    <p>By signing below, the parties agree to the terms of this lease.</p>
    <p>Landlord: ${chip('company.signatoryName')}, ${chip('company.signatoryTitle')}</p>
    <p>Tenant: ${chip('tenant.primaryName')}</p>
  `,

  // Same "token mismatch is deliberate" posture as DECLINE_NOTICE above:
  // owner.names/mailingAddress and statement.* aren't in NOTICE_MERGE_TOKEN_GROUPS
  // (no OWNER_STATEMENT-specific token group exists yet — see this kind's
  // note in DocumentTemplateList.tsx), so only the company.* chips, which
  // are correct regardless, are inserted here. The itemized line table and
  // totals are fixed PDF layout the generator draws itself (see
  // generateOwnerStatementPdf), not merge-field text — this body is only
  // the letter's free-text header/footer wording.
  OWNER_STATEMENT: `
    <p>Dear [Owner Name],</p>
    <p>Enclosed is your owner statement for the period [Period Start] through [Period End].</p>
    <p>The itemized breakdown of rental income, fees, and any adjustments for this period follows below
    this letter, along with your running account balance.</p>
    <p>If you have any questions about this statement, please contact us at ${chip('company.phone')} or
    ${chip('company.contactEmail')}.</p>
    <p>Sincerely,<br>${chip('company.signatoryName')}, ${chip('company.signatoryTitle')}<br>${chip('company.legalName')}</p>
  `,

  // Deliberately short — this is the ONLY editable part of the email. The
  // CTA button (its label and URL) and the expiry footer are fixed, code-
  // rendered chrome around this body, not part of this template — see
  // DocumentTemplateKind's doc comment in schema.prisma.
  PORTAL_INVITE_EMAIL: `
    <p>Hi ${chip('recipient.name')},</p>
    <p>${chip('company.legalName')} has set up a portal account for you. Use the button below to choose a
    password and sign in.</p>
  `,

  PASSWORD_RESET_EMAIL: `
    <p>Hi ${chip('recipient.name')},</p>
    <p>We received a request to reset your ${chip('company.legalName')} password. Use the button below to
    choose a new one.</p>
  `,
};
